// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// API keys: list, create (secret shown once), and revoke with typed confirmation.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Ban, KeyRound, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { applicationQuery } from '@/api/queries/applications'
import type { ApiKeyView } from '@/api/types'
import { Can } from '@/auth/Can'
import { usePermissions } from '@/auth/usePermissions'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { DataTable, tableColumns } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tooltip } from '@/components/ui/tooltip'
import { apiKeysQuery, useRevokeApiKey } from '@/features/ops/api'
import { CreateApiKeyDialog } from '@/features/ops/CreateApiKeyDialog'
import { LiveCredentialsRefusedNotice, ModeBadge } from '@/features/ops/LiveSending'
import { formatDateTime } from '@/lib/format'

export const Route = createFileRoute('/applications/$appId/keys')({
  staticData: { crumb: 'API keys' },
  validateSearch: (s: Record<string, unknown>): { inactive?: boolean } => ({
    inactive: s.inactive === true || s.inactive === 'true' ? true : undefined,
  }),
  component: AppKeysPage,
})

const STATUS = {
  active: <Badge tone="success">Active</Badge>,
  revoked: <Badge tone="danger">Revoked</Badge>,
  expired: <Badge tone="warning">Expired</Badge>,
} as const

const col = tableColumns<ApiKeyView>()

function buildColumns(onRevoke: (k: ApiKeyView) => void, canManage: boolean) {
  return col.columns([
    col.accessor('name', {
      header: 'Key',
      cell: (c) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">{c.getValue()}</div>
          <div className="truncate font-mono text-xs text-ink-2">{c.row.original.display}</div>
        </div>
      ),
      enableHiding: false,
    }),
    col.accessor('mode', {
      header: 'Mode',
      cell: (c) => <ModeBadge mode={c.getValue()} />,
      meta: { width: '6rem' },
    }),
    col.accessor('scopes', {
      header: 'Scopes',
      cell: (c) => <span className="text-ink-2">{c.getValue().join(', ')}</span>,
      enableSorting: false,
      meta: { width: '8rem' },
    }),
    col.accessor('status', {
      header: 'Status',
      cell: (c) => STATUS[c.getValue()],
      meta: { width: '7rem' },
    }),
    col.accessor('last_used_at', {
      header: 'Last used',
      cell: (c) =>
        c.getValue() ? (
          <RelativeTime value={c.getValue()} className="text-ink-2" />
        ) : (
          <span className="text-ink-3">Never</span>
        ),
      sortFn: 'datetime',
      meta: { width: '8rem' },
    }),
    col.accessor('expires_at', {
      header: 'Expires',
      cell: (c) => {
        const k = c.row.original
        if (k.revoked_at)
          return (
            <Tooltip content={`Revoked ${formatDateTime(k.revoked_at)}`}>
              <span className="text-ink-3">
                Revoked <RelativeTime value={k.revoked_at} />
              </span>
            </Tooltip>
          )
        return c.getValue() ? (
          <RelativeTime
            value={c.getValue()}
            className={k.status === 'expired' ? 'text-warning' : 'text-ink-2'}
          />
        ) : (
          <span className="text-ink-3">Never</span>
        )
      },
      meta: { width: '9rem' },
    }),
    col.accessor('created_at', {
      header: 'Created',
      cell: (c) => (
        <div className="min-w-0">
          <RelativeTime value={c.getValue()} className="text-ink-2" />
          <div className="truncate text-xs text-ink-3">by {c.row.original.created_by}</div>
        </div>
      ),
      sortFn: 'datetime',
      meta: { width: '9rem' },
    }),
    col.display({
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: (c) =>
        canManage && c.row.original.status === 'active' ? (
          <Button
            size="xs"
            variant="danger-ghost"
            icon={<Ban />}
            onClick={() => onRevoke(c.row.original)}
          >
            Revoke
          </Button>
        ) : null,
      enableHiding: false,
      meta: { align: 'right', width: '6.5rem', label: '' },
    }),
  ])
}

function AppKeysPage() {
  const { appId } = Route.useParams()
  const { inactive = false } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { can } = usePermissions()
  const [creating, setCreating] = useState(false)
  const [revoking, setRevoking] = useState<ApiKeyView | undefined>()
  const keys = useQuery(apiKeysQuery(appId, inactive))
  const app = useQuery(applicationQuery(appId))
  const refusedLiveKeys =
    app.data && !app.data.live_enabled
      ? (keys.data ?? []).filter((k) => k.mode === 'live' && k.status === 'active').length
      : 0
  const revoke = useRevokeApiKey(appId)
  const canManage = can('manage_config')
  const columns = useMemo(() => buildColumns(setRevoking, canManage), [canManage])

  return (
    <div className="space-y-3">
      <LiveCredentialsRefusedNotice
        appId={appId}
        count={refusedLiveKeys}
        noun={{ one: 'key', many: 'keys' }}
      />
      <DataTable
        aria-label="API keys"
        columns={columns}
        data={keys.data ?? []}
        getRowId={(k) => k.id}
        isLoading={keys.isPending}
        error={keys.error}
        onRetry={() => void keys.refetch()}
        persistKey="app-keys"
        toolbar={
          <>
            <div className="flex items-center gap-2">
              <Switch
                id="show-inactive"
                checked={inactive}
                onCheckedChange={(v) =>
                  void navigate({
                    search: (prev) => ({ ...prev, inactive: v || undefined }),
                    replace: true,
                  })
                }
              />
              <Label htmlFor="show-inactive" className="font-normal text-ink-2">
                Show revoked and expired keys
              </Label>
            </div>
            <div className="ml-auto">
              <Can permission="manage_config" mode="disable">
                <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                  Create API key
                </Button>
              </Can>
            </div>
          </>
        }
        empty={
          <EmptyState
            icon={KeyRound}
            title={inactive ? 'No API keys' : 'No active API keys'}
            description="Create a test key to start integrating. Issue a live key when the application is ready to email real customers."
            action={
              canManage ? (
                <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                  Create API key
                </Button>
              ) : undefined
            }
          />
        }
      />
      <p className="text-xs text-ink-3">
        Services with an Entra managed identity can call the API without a key. Add them under{' '}
        <Link
          to="/applications/$appId/settings"
          params={{ appId }}
          hash="workload-identities"
          className="text-brass hover:underline"
        >
          Settings, Workload identities
        </Link>
        .
      </p>
      <CreateApiKeyDialog appId={appId} open={creating} onOpenChange={setCreating} />
      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(o) => !o && setRevoking(undefined)}
        title={`Revoke ${revoking?.name ?? 'key'}?`}
        description={
          <>
            Requests using <span className="font-mono">{revoking?.display}</span> fail immediately.
            This can’t be undone; create a new key to restore access.
          </>
        }
        confirmLabel="Revoke key"
        destructive
        onConfirm={() => revoking && revoke.mutateAsync(revoking.id)}
      />
    </div>
  )
}
